import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { TrendingUp, Users, CalendarCheck, Star, Volume2, VolumeX } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const AD_VIDEO = "/ad-video.mp4";
const AD_POSTER = "/ad-poster.jpg";

export default function Results() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    setMuted(next);
    if (!next) {
      v.play().catch(() => {});
    }
  };

  return (
    <section id="results" className="bg-slate-50 py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="mb-16 max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-blue-deep">/ Clinical results</span>
          <h2 className="mt-4 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-obsidian md:text-5xl">
            The numbers our healthcare professionals &amp; wellness brands love to read.
          </h2>
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch">
          {/* Stats */}
          <div className="grid flex-1 grid-cols-2 gap-4 sm:auto-rows-[minmax(150px,1fr)]">
            {/* big stat */}
            <motion.div custom={0} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
              data-testid="result-1"
              className="col-span-2 flex flex-col justify-between rounded-3xl bg-obsidian p-8 text-white">
              <TrendingUp className="h-8 w-8 text-lab-green" />
              <div>
                <p className="font-display text-6xl font-extrabold md:text-7xl">+312%</p>
                <p className="mt-2 text-slate-400">Average growth in profile reach within 90 days of onboarding.</p>
              </div>
            </motion.div>

            <motion.div custom={1} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
              data-testid="result-2"
              className="flex flex-col justify-between rounded-3xl border border-slate-200 bg-white p-6">
              <Users className="h-7 w-7 text-blue-brand" />
              <div>
                <p className="font-display text-4xl font-extrabold text-obsidian">48k+</p>
                <p className="mt-1 text-sm text-slate-500">New followers earned</p>
              </div>
            </motion.div>

            <motion.div custom={2} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
              data-testid="result-3"
              className="flex flex-col justify-between rounded-3xl border border-slate-200 bg-lab-light p-6">
              <CalendarCheck className="h-7 w-7 text-lab-deep" />
              <div>
                <p className="font-display text-4xl font-extrabold text-obsidian">2.4x</p>
                <p className="mt-1 text-sm text-lab-deep">More booked appointments</p>
              </div>
            </motion.div>

            <motion.div custom={4} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
              data-testid="result-4"
              className="col-span-2 flex items-center justify-between rounded-3xl border border-slate-200 bg-white p-6">
              <div>
                <p className="font-display text-4xl font-extrabold text-obsidian">4.9<span className="text-lab-green">/5</span></p>
                <p className="mt-1 text-sm text-slate-500">Avg. Google rating we help maintain</p>
              </div>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-lab-green text-lab-green" />)}
              </div>
            </motion.div>
          </div>

          {/* Reel — full 9:16 video */}
          <motion.div custom={3} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
            data-testid="result-image"
            className="relative mx-auto aspect-[9/16] w-full max-w-[300px] overflow-hidden rounded-3xl border border-slate-200 bg-obsidian lg:mx-0 lg:aspect-auto lg:w-[300px] lg:max-w-none lg:flex-shrink-0">
            <video
              ref={videoRef}
              src={AD_VIDEO}
              poster={AD_POSTER}
              autoPlay
              muted
              loop
              playsInline
              data-testid="result-video"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <button
              type="button"
              onClick={toggleSound}
              aria-label={muted ? "Unmute video" : "Mute video"}
              data-testid="result-video-sound"
              className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-obsidian shadow-md backdrop-blur transition-transform duration-300 hover:-translate-y-0.5"
            >
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <div className="absolute bottom-4 left-4 rounded-full bg-white/90 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-obsidian backdrop-blur">
              40+ practices scaled
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
